import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import { Select } from '../../components/select/Select';
import { DescriptionHeader } from '../../features/user/components/description-header';
import { getCurrentMember } from '../../lib/utils/get-current-member';
import { toastPool } from '../../features/toasts/models/toast-pool';
import { Toast } from '../../features/toasts/models/toast';
import { catchError } from '../../core/catch-error';
import { uiStore } from '../../core/stores/ui-store';

import type { Member } from '../../app/entities';

import styles from './preferences.page.module.scss';
const timeFormatOptions = [
  { value: '12h', label: '12-hour (2:30pm)' },
  { value: '24h', label: '24-hour (14:30)' },
];

const commentSortOptions = [
  { value: 'timestamp', label: 'Timecode' },
  { value: 'createdAt', label: 'Oldest first' },
  { value: '-createdAt', label: 'Newest first' },
];

export const PreferencesPage = observer(() => {
  const member = getCurrentMember();

  useEffect(() => {
    uiStore.pageTitle = {
      label: 'Preferences',
    };

    return () => {
      uiStore.pageTitle = {
        label: '',
      };
    };
  });

  const handleTimeFormatChange = async ({ target }: { target: { value: string } }) => {
    const timeFormat = target.value as '12h' | '24h';

    if (!member || timeFormat === member.preferences?.timeFormat) {
      return;
    }

    try {
      await member.update({
        preferences: {
          ...member.preferences,
          timeFormat,
        },
      } as Partial<Omit<Member, '_id'>>);
      toastPool.insert(new Toast('Your settings are updated successfully'));
    } catch (e) {
      catchError(e);
    }
  };

  const handleCommentSortChange = async ({ target }: { target: { value: string } }) => {
    const commentSortOrder = target.value as Member['preferences']['commentSortOrder'];

    if (!member || commentSortOrder === member.preferences?.commentSortOrder) {
      return;
    }

    try {
      await member.update({
        preferences: {
          ...member.preferences,
          commentSortOrder,
        },
      } as Partial<Omit<Member, '_id'>>);
      toastPool.insert(new Toast('Your settings are updated successfully'));
    } catch (e) {
      catchError(e);
    }
  };

  return (
    <>
      <DescriptionHeader
        title="Preferences"
        description="Customize how times and comments are displayed in your current workspace"
      />

      <div className={styles['field-container']}>
        <Select
          name="timeFormat"
          value={member?.preferences?.timeFormat === '24h' ? '24h' : '12h'}
          onSelect={handleTimeFormatChange}
          options={timeFormatOptions}
          label="Time format"
          disabled={!member}
        />
      </div>

      <div className={styles['field-container']}>
        <Select
          name="commentSortOrder"
          value={member?.preferences?.commentSortOrder || 'timestamp'}
          onSelect={handleCommentSortChange}
          options={commentSortOptions}
          label="Default comment sort order"
          disabled={!member}
        />
      </div>
    </>
  );
});
